const { Criteria } = Shopware.Data;

const STATUS = { verified: 'bestätigt', pending: 'offen', rejected: 'abgelehnt' };
const METHOD = { mrz: 'Ausweisdaten', upload: 'Upload', manual: 'manuell geprüft' };

export default {
    template: `
<sw-page class="riccardo-age-check-list">
    <template #smart-bar-header><h2>Altersprüfungen <span v-if="!isLoading">({{ total }})</span></h2></template>
    <template #smart-bar-actions>
        <sw-single-select v-model:value="statusFilter" :options="statusOptions" label="" style="min-width: 220px" @update:value="getList" />
    </template>
    <template #content>
        <sw-entity-listing v-if="records" :items="records" :repository="repository" :columns="columns" :is-loading="isLoading"
                           :show-selection="false" :allow-inline-edit="false" :allow-column-edit="false" @page-change="onPageChange">
            <template #column-createdAt="{ item }">{{ formatDate(item.createdAt) }}</template>
            <template #column-customer="{ item }">
                <router-link v-if="item.customer" :to="{ name: 'sw.customer.detail', params: { id: item.customerId } }">
                    {{ item.customer.firstName }} {{ item.customer.lastName }}
                </router-link>
                <span v-else style="color:#798490">Gast</span>
            </template>
            <template #column-status="{ item }">
                <sw-label :variant="item.status === 'verified' ? 'success' : (item.status === 'pending' ? 'warning' : 'danger')" appearance="pill">
                    {{ statusLabel(item.status) }}
                </sw-label>
            </template>
            <template #column-method="{ item }">{{ methodLabel(item.method) }}</template>
            <template #column-ageYears="{ item }">{{ item.ageYears ? item.ageYears + ' Jahre' : '–' }}</template>
            <template #column-document="{ item }">
                <span v-if="item.documentType">{{ item.documentType === 'passport' ? 'Pass' : 'Ausweis' }}{{ item.documentCountry ? ' · ' + item.documentCountry : '' }}</span>
                <span v-else>–</span>
            </template>
            <template #column-expiresAt="{ item }">{{ item.expiresAt ? formatDate(item.expiresAt) : (item.status === 'verified' ? 'unbegrenzt' : '–') }}</template>
            <template #column-orderNumber="{ item }">{{ item.orderNumber || '' }}</template>
            <template #actions="{ item }">
                <sw-context-menu-item v-if="item.mediaId" @click="openDocument(item)">Dokument ansehen</sw-context-menu-item>
                <sw-context-menu-item v-if="item.status === 'pending'" @click="decide(item, true)">Freigeben</sw-context-menu-item>
                <sw-context-menu-item v-if="item.status === 'pending'" variant="danger" @click="decide(item, false)">Ablehnen</sw-context-menu-item>
                <sw-context-menu-item v-if="item.mediaId" variant="danger" @click="deleteDocument(item)">Dokument löschen</sw-context-menu-item>
            </template>
        </sw-entity-listing>
        <sw-empty-state v-if="records && !records.length" title="Keine Einträge" subline="Hier erscheinen die Altersprüfungen aus dem Shop." />
    </template>
</sw-page>`,

    inject: ['repositoryFactory', 'loginService'],

    mixins: [Shopware.Mixin.getByName('notification')],

    data() {
        return { records: null, isLoading: false, total: 0, page: 1, limit: 25, statusFilter: 'all' };
    },

    computed: {
        repository() {
            return this.repositoryFactory.create('riccardo_age_verification');
        },
        statusOptions() {
            return [
                { value: 'all', label: 'Alle Prüfungen' },
                { value: 'pending', label: 'Nur offene (Upload)' },
                { value: 'verified', label: 'Nur bestätigte' },
                { value: 'rejected', label: 'Nur abgelehnte' },
            ];
        },
        columns() {
            return [
                { property: 'createdAt', label: 'Zeitpunkt', primary: true },
                { property: 'customer', label: 'Kunde', sortable: false },
                { property: 'status', label: 'Status' },
                { property: 'method', label: 'Verfahren' },
                { property: 'document', label: 'Dokument', sortable: false },
                { property: 'ageYears', label: 'Alter' },
                { property: 'expiresAt', label: 'Gültig bis' },
                { property: 'orderNumber', label: 'Bestellung' },
            ];
        },
        httpClient() {
            return Shopware.Application.getContainer('init').httpClient;
        },
        authHeaders() {
            return { Authorization: `Bearer ${this.loginService.getToken()}` };
        },
    },

    created() {
        this.getList();
    },

    methods: {
        statusLabel(status) {
            return STATUS[status] || status;
        },
        methodLabel(method) {
            return METHOD[method] || method;
        },
        formatDate(value) {
            return value ? new Date(value).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' }) : '';
        },

        onPageChange({ page, limit }) {
            this.page = page;
            this.limit = limit;
            this.getList();
        },

        getList() {
            this.isLoading = true;
            const criteria = new Criteria(this.page, this.limit);
            criteria.addAssociation('customer');
            criteria.addSorting(Criteria.sort('createdAt', 'DESC'));
            criteria.setTotalCountMode(1);
            if (this.statusFilter !== 'all') {
                criteria.addFilter(Criteria.equals('status', this.statusFilter));
            }
            return this.repository.search(criteria).then((result) => {
                this.records = result;
                this.total = result.total;
            }).finally(() => { this.isLoading = false; });
        },

        openDocument(item) {
            return this.httpClient.get(`_action/riccardo-age-check/${item.id}/document`, {
                headers: this.authHeaders,
                responseType: 'blob',
            }).then(({ data }) => {
                const url = URL.createObjectURL(data);
                window.open(url, '_blank', 'noopener');
                window.setTimeout(() => URL.revokeObjectURL(url), 60000);
            }).catch(() => {
                this.createNotificationError({ message: 'Dokument konnte nicht geladen werden (vielleicht schon gelöscht).' });
            });
        },

        decide(item, approve) {
            return this.httpClient.post(`_action/riccardo-age-check/${item.id}/decide`, {
                approve,
                reason: approve ? '' : 'Im Admin abgelehnt',
            }, { headers: this.authHeaders }).then(() => {
                this.createNotificationSuccess({ message: approve ? 'Prüfung freigegeben' : 'Prüfung abgelehnt' });
                return this.getList();
            }).catch(() => {
                this.createNotificationError({ message: 'Die Entscheidung konnte nicht gespeichert werden.' });
            });
        },

        deleteDocument(item) {
            return this.httpClient.delete(`_action/riccardo-age-check/${item.id}/document`, { headers: this.authHeaders })
                .then(() => {
                    this.createNotificationSuccess({ message: 'Dokument gelöscht' });
                    return this.getList();
                });
        },
    },
};
